import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { fetchWithAuth } from '@/lib/api'
import { normalizeExplorerPath } from '@/lib/explorer-tree-focus'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageKeys, StorageSchemas } from '@/lib/storage'
import {
  applyFolderBrowseMode,
  BROWSE_FOCUS_DEFAULTS,
  buildFolderActivityMap,
  buildFolderVisibilityContext,
  countHiddenUnderParent,
  countHiddenUnderParentBreakdown,
  filterChildrenByBrowseFocus,
  filterChildrenManualHideOnly,
  folderBrowseModeFromPrefs,
  formatHiddenChildrenLabel,
  parentQualifiesForFocus,
  resolveFolderChildVisibilities,
  type BrowseFolderPrefs,
  type BrowseInactiveDays,
  type BrowseTreeVisibilitySession,
  type FolderBrowseMode,
  type FolderBrowseVisibility,
} from '@/lib/browse-folder-focus'
import type { ExplorerFolderTreeNode } from '@/components/layout/explorer-inline-folder-node'

type BrowseFolderFocusContextValue = {
  focusBrowse: boolean
  setFocusBrowse: (v: boolean) => void
  inactiveDays: BrowseInactiveDays
  setInactiveDays: (d: BrowseInactiveDays) => void
  showActivityInTree: boolean
  setShowActivityInTree: (v: boolean) => void
  focusMinChildren: number
  setFocusMinChildren: (n: number) => void
  focusMaxVisible: number
  setFocusMaxVisible: (n: number) => void
  parentQualifiesForFocus: (children: { type: string }[] | undefined) => boolean
  getFolderMode: (folderPath: string) => FolderBrowseMode
  setFolderMode: (folderPath: string, mode: FolderBrowseMode) => void
  getFolderVisibility: (
    folderPath: string,
    parentPath: string,
    isOnActivePath: boolean,
    siblings?: { path: string; type: string }[]
  ) => FolderBrowseVisibility
  filterFolderChildren: (
    children: ExplorerFolderTreeNode[] | undefined,
    parentPath: string,
    activeDocumentPath: string | null,
    openTabPaths: string[]
  ) => ExplorerFolderTreeNode[]
  countHiddenChildren: (
    children: ExplorerFolderTreeNode[] | undefined,
    parentPath: string,
    activeDocumentPath: string | null,
    openTabPaths: string[]
  ) => number
  hiddenChildrenBreakdown: (
    children: ExplorerFolderTreeNode[] | undefined,
    parentPath: string,
    activeDocumentPath: string | null,
    openTabPaths: string[]
  ) => { folders: number; files: number; total: number; label: string }
  activityDaysLabel: (path: string) => string | null
  refreshActivity: () => void
  /** Merge folder max-updated times from explorer-tree metadata (fallback when not in recent cap). */
  mergeTreeFolderActivity: (entries: { folderPath: string; timestampMs: number }[]) => void
  isBrowseParentRevealed: (parentPath: string) => boolean
  revealBrowseParent: (parentPath: string) => void
  collapseBrowseParent: (parentPath: string) => void
  /** Re-enable focus filter (per-parent reveal cleared when turning focus on). */
  restoreBrowseFocusFilter: () => void
  /** BrowsePanel: expanded folders + hub folder path for visibility while hub is open. */
  registerBrowseTreeSession: (session: BrowseTreeVisibilitySession) => void
}

const BrowseFolderFocusContext = createContext<BrowseFolderFocusContextValue | null>(null)

/** Stable empty array — never use `openTabPaths = []` in props (new ref every render). */
const EMPTY_OPEN_TAB_PATHS: string[] = []

export function useBrowseFolderFocus(): BrowseFolderFocusContextValue | null {
  return useContext(BrowseFolderFocusContext)
}

export function useBrowseFolderFocusRequired(): BrowseFolderFocusContextValue {
  const ctx = useContext(BrowseFolderFocusContext)
  if (!ctx) {
    throw new Error('useBrowseFolderFocusRequired must be used within BrowseFolderFocusProvider')
  }
  return ctx
}

type BrowseFolderFocusProviderProps = {
  children: ReactNode
  workspaceIds: string[]
  activeDocumentPath?: string | null
  openTabPaths?: string[]
  /** Prototype/tests: skip API and seed activity timestamps. */
  activitySeedEntries?: { filePath: string; timestampMs: number }[]
}

export function BrowseFolderFocusProvider({
  children,
  workspaceIds,
  activeDocumentPath = null,
  openTabPaths: openTabPathsProp,
  activitySeedEntries,
}: BrowseFolderFocusProviderProps) {
  const openTabPaths = openTabPathsProp ?? EMPTY_OPEN_TAB_PATHS
  const workspaceIdsKey = workspaceIds.join('\0')
  const openTabPathsKey = openTabPaths.join('\0')
  const activitySeedKey = activitySeedEntries
    ? activitySeedEntries.map((e) => e.filePath).join('\0')
    : ''
  const [focusBrowse, setFocusBrowseState] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_ENABLED,
    defaultValue: BROWSE_FOCUS_DEFAULTS.focusBrowse,
    schema: StorageSchemas.boolean,
  })
  const [inactiveDays, setInactiveDays] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_INACTIVE_DAYS,
    defaultValue: BROWSE_FOCUS_DEFAULTS.inactiveDays,
    schema: StorageSchemas.number,
  })
  const [showActivityInTree, setShowActivityInTree] = usePersistedState({
    key: StorageKeys.BROWSE_SHOW_ACTIVITY_IN_TREE,
    defaultValue: BROWSE_FOCUS_DEFAULTS.showActivityInTree,
    schema: StorageSchemas.boolean,
  })
  const [focusMinChildren, setFocusMinChildren] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_MIN_CHILDREN,
    defaultValue: BROWSE_FOCUS_DEFAULTS.focusMinChildren,
    schema: StorageSchemas.number,
  })
  const [focusMaxVisible, setFocusMaxVisible] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_MAX_VISIBLE,
    defaultValue: BROWSE_FOCUS_DEFAULTS.focusMaxVisible,
    schema: StorageSchemas.number,
  })
  const [folderPrefs, setFolderPrefs] = usePersistedState({
    key: StorageKeys.BROWSE_FOLDER_PREFS,
    defaultValue: { alwaysShowPaths: [] as string[], hiddenManualPaths: [] as string[] },
    schema: StorageSchemas.browseFolderPrefs,
  })

  const [activityMap, setActivityMap] = useState<Map<string, number>>(() => new Map())
  const [revealedParentPaths, setRevealedParentPaths] = useState<Set<string>>(() => new Set())

  const setFocusBrowse = useCallback(
    (v: boolean) => {
      setFocusBrowseState(v)
      if (v) {
        setRevealedParentPaths(new Set())
      }
    },
    [setFocusBrowseState]
  )

  const [browseTreeSession, setBrowseTreeSession] = useState<BrowseTreeVisibilitySession>({
    expandedFolderPaths: [],
    contextFolderPaths: [],
  })

  const registerBrowseTreeSession = useCallback((session: BrowseTreeVisibilitySession) => {
    setBrowseTreeSession((prev) => {
      const expandedFolderPaths = session.expandedFolderPaths ?? []
      const contextFolderPaths = session.contextFolderPaths ?? []
      const prevExp = prev.expandedFolderPaths ?? []
      const prevCtx = prev.contextFolderPaths ?? []
      if (
        prevExp.length === expandedFolderPaths.length &&
        prevCtx.length === contextFolderPaths.length &&
        prevExp.every((p, i) => p === expandedFolderPaths[i]) &&
        prevCtx.every((p, i) => p === contextFolderPaths[i])
      ) {
        return prev
      }
      return { expandedFolderPaths, contextFolderPaths }
    })
  }, [])

  const prefs = folderPrefs as BrowseFolderPrefs

  const isBrowseParentRevealed = useCallback(
    (parentPath: string) => revealedParentPaths.has(normalizeExplorerPath(parentPath)),
    [revealedParentPaths]
  )

  const revealBrowseParent = useCallback((parentPath: string) => {
    const p = normalizeExplorerPath(parentPath)
    setRevealedParentPaths((prev) => new Set(prev).add(p))
  }, [])

  const collapseBrowseParent = useCallback((parentPath: string) => {
    const p = normalizeExplorerPath(parentPath)
    setRevealedParentPaths((prev) => {
      const next = new Set(prev)
      next.delete(p)
      return next
    })
  }, [])

  const restoreBrowseFocusFilter = useCallback(() => {
    setFocusBrowse(true)
    setRevealedParentPaths(new Set())
  }, [setFocusBrowse])

  const browseSettings = useMemo(
    () => ({
      focusBrowse,
      inactiveDays: inactiveDays as number,
      focusMinChildren: focusMinChildren as number,
      focusMaxVisible: focusMaxVisible as number,
      prefs,
    }),
    [focusBrowse, inactiveDays, focusMinChildren, focusMaxVisible, prefs]
  )

  const makeVisibilityContext = useCallback(
    (activeDocPath: string | null, tabPaths: string[]) =>
      buildFolderVisibilityContext(
        browseSettings,
        activityMap,
        activeDocPath,
        tabPaths,
        browseTreeSession
      ),
    [browseSettings, activityMap, browseTreeSession]
  )

  const refreshActivity = useCallback(() => {
    if (!focusBrowse) return
    if (activitySeedEntries?.length) {
      const now = Date.now()
      const entries = [...activitySeedEntries]
      for (const tabPath of openTabPaths) {
        if (tabPath) entries.push({ filePath: tabPath, timestampMs: now })
      }
      if (activeDocumentPath) {
        entries.push({ filePath: activeDocumentPath, timestampMs: now })
      }
      setActivityMap((prev) => {
        const nextMap = mergeActivityMaps(buildFolderActivityMap(entries), prev)
        return mapsEqual(prev, nextMap) ? prev : nextMap
      })
      return
    }
    if (workspaceIds.length === 0) return
    const ws = workspaceIds[0]
    void (async () => {
      try {
        const res = await fetchWithAuth(
          `/api/documents/recent?limit=400&sortBy=viewed&workspaceId=${encodeURIComponent(ws)}`
        )
        if (!res.ok) return
        const data = await res.json()
        const docs = (data.documents ?? data.items ?? []) as {
          file_path?: string
          filePath?: string
          sort_timestamp?: string
          sortTimestamp?: string | Date
          last_viewed_at?: string
          lastViewedAt?: string | Date
          updated_at?: string
          updatedAtRaw?: string | Date
        }[]
        const entries: { filePath: string; timestampMs: number }[] = []
        const now = Date.now()
        for (const d of docs) {
          const fp = d.file_path ?? d.filePath
          if (!fp) continue
          const raw =
            d.sort_timestamp ??
            d.sortTimestamp ??
            d.last_viewed_at ??
            d.lastViewedAt ??
            d.updated_at ??
            d.updatedAtRaw
          const ts = raw ? new Date(raw).getTime() : NaN
          if (!Number.isFinite(ts)) continue
          entries.push({ filePath: fp, timestampMs: ts })
        }
        for (const tabPath of openTabPaths) {
          if (tabPath) entries.push({ filePath: tabPath, timestampMs: now })
        }
        if (activeDocumentPath) {
          entries.push({ filePath: activeDocumentPath, timestampMs: now })
        }
        setActivityMap((prev) => {
          const nextMap = mergeActivityMaps(buildFolderActivityMap(entries), prev)
          return mapsEqual(prev, nextMap) ? prev : nextMap
        })
      } catch {
        /* non-critical */
      }
    })()
  }, [focusBrowse, workspaceIdsKey, openTabPathsKey, activeDocumentPath, activitySeedKey, activitySeedEntries, openTabPaths])

  useEffect(() => {
    refreshActivity()
  }, [refreshActivity])

  const mergeTreeFolderActivity = useCallback((entries: { folderPath: string; timestampMs: number }[]) => {
    if (!entries.length) return
    setActivityMap((prev) => {
      const next = new Map(prev)
      for (const { folderPath, timestampMs } of entries) {
        if (!Number.isFinite(timestampMs) || timestampMs <= 0) continue
        const p = normalizeExplorerPath(folderPath)
        next.set(p, Math.max(next.get(p) ?? 0, timestampMs))
        const parts = p.split('/').filter(Boolean)
        let prefix = ''
        for (let i = 0; i < parts.length; i++) {
          prefix = prefix ? `${prefix}/${parts[i]}` : parts[i]
          next.set(prefix, Math.max(next.get(prefix) ?? 0, timestampMs))
        }
      }
      return mapsEqual(prev, next) ? prev : next
    })
  }, [])

  const getFolderMode = useCallback(
    (folderPath: string) => folderBrowseModeFromPrefs(folderPath, prefs),
    [prefs]
  )

  const setFolderMode = useCallback(
    (folderPath: string, mode: FolderBrowseMode) => {
      setFolderPrefs(applyFolderBrowseMode(prefs, folderPath, mode))
    },
    [prefs, setFolderPrefs]
  )

  const getFolderVisibility = useCallback(
    (
      folderPath: string,
      _parentPath: string,
      isOnActivePath: boolean,
      siblings?: { path: string; type: string }[]
    ) => {
      if (siblings?.length) {
        const ctx = makeVisibilityContext(activeDocumentPath, openTabPaths)
        const vis = resolveFolderChildVisibilities(siblings, ctx)
        return vis.get(folderPath) ?? 'visible'
      }
      const ctx = makeVisibilityContext(activeDocumentPath, openTabPaths)
      return resolveFolderChildVisibilities(
        [{ path: folderPath, type: 'folder' }],
        { ...ctx, isOnActivePath: () => isOnActivePath }
      ).get(folderPath) ?? 'visible'
    },
    [makeVisibilityContext, activeDocumentPath, openTabPaths]
  )

  const filterFolderChildren = useCallback(
    (
      children: ExplorerFolderTreeNode[] | undefined,
      parentPath: string,
      activeDocPath: string | null,
      tabPaths: string[]
    ) => {
      if (!children?.length) return []
      if (!focusBrowse) return children
      if (isBrowseParentRevealed(parentPath)) {
        return filterChildrenManualHideOnly(children, prefs)
      }
      const ctx = makeVisibilityContext(activeDocPath, tabPaths)
      return filterChildrenByBrowseFocus(children, ctx)
    },
    [makeVisibilityContext, focusBrowse, isBrowseParentRevealed, prefs]
  )

  const hiddenChildrenBreakdown = useCallback(
    (
      children: ExplorerFolderTreeNode[] | undefined,
      _parentPath: string,
      activeDocPath: string | null,
      tabPaths: string[]
    ) => {
      if (!children?.length) {
        return { folders: 0, files: 0, total: 0, label: '' }
      }
      if (!focusBrowse || isBrowseParentRevealed(_parentPath)) {
        return { folders: 0, files: 0, total: 0, label: '' }
      }
      const ctx = makeVisibilityContext(activeDocPath, tabPaths)
      const breakdown = countHiddenUnderParentBreakdown(children, ctx)
      return { ...breakdown, label: formatHiddenChildrenLabel(breakdown) }
    },
    [makeVisibilityContext, focusBrowse, isBrowseParentRevealed]
  )

  const countHiddenChildren = useCallback(
    (
      children: ExplorerFolderTreeNode[] | undefined,
      parentPath: string,
      activeDocPath: string | null,
      tabPaths: string[]
    ) => hiddenChildrenBreakdown(children, parentPath, activeDocPath, tabPaths).total,
    [hiddenChildrenBreakdown]
  )

  const parentQualifiesForFocusFn = useCallback(
    (children: { type: string }[] | undefined) => {
      if (!children?.length) return false
      return parentQualifiesForFocus(children, browseSettings)
    },
    [browseSettings]
  )

  const activityDaysLabel = useCallback(
    (folderPath: string) => {
      if (!showActivityInTree) return null
      const ms = activityMap.get(normalizeExplorerPath(folderPath))
      if (ms == null) return '-'
      const days = Math.floor((Date.now() - ms) / 86_400_000)
      return `${days}d`
    },
    [showActivityInTree, activityMap]
  )

  const value = useMemo(
    (): BrowseFolderFocusContextValue => ({
      focusBrowse,
      setFocusBrowse,
      inactiveDays: inactiveDays as BrowseInactiveDays,
      setInactiveDays: (d) => setInactiveDays(d),
      showActivityInTree,
      setShowActivityInTree,
      focusMinChildren: focusMinChildren as number,
      setFocusMinChildren,
      focusMaxVisible: focusMaxVisible as number,
      setFocusMaxVisible,
      parentQualifiesForFocus: parentQualifiesForFocusFn,
      getFolderMode,
      setFolderMode,
      getFolderVisibility,
      filterFolderChildren,
      countHiddenChildren,
      hiddenChildrenBreakdown,
      activityDaysLabel,
      refreshActivity,
      mergeTreeFolderActivity,
      isBrowseParentRevealed,
      revealBrowseParent,
      collapseBrowseParent,
      restoreBrowseFocusFilter,
      registerBrowseTreeSession,
    }),
    [
      focusBrowse,
      setFocusBrowse,
      inactiveDays,
      setInactiveDays,
      showActivityInTree,
      setShowActivityInTree,
      focusMinChildren,
      setFocusMinChildren,
      focusMaxVisible,
      setFocusMaxVisible,
      parentQualifiesForFocusFn,
      getFolderMode,
      setFolderMode,
      getFolderVisibility,
      filterFolderChildren,
      countHiddenChildren,
      hiddenChildrenBreakdown,
      activityDaysLabel,
      refreshActivity,
      mergeTreeFolderActivity,
      isBrowseParentRevealed,
      revealBrowseParent,
      collapseBrowseParent,
      restoreBrowseFocusFilter,
      registerBrowseTreeSession,
    ]
  )

  return (
    <BrowseFolderFocusContext.Provider value={value}>
      {children}
    </BrowseFolderFocusContext.Provider>
  )
}

function mapsEqual(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) return false
  for (const [k, v] of a) {
    if (b.get(k) !== v) return false
  }
  return true
}

/** Keep explorer-tree folder max-updated when recent fetch completes (do not replace whole map). */
function mergeActivityMaps(
  fromRecent: Map<string, number>,
  prior: Map<string, number>
): Map<string, number> {
  const merged = new Map(fromRecent)
  for (const [path, ms] of prior) {
    merged.set(path, Math.max(merged.get(path) ?? 0, ms))
  }
  return merged
}
